import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "../i18n";
import { useSeo } from "../hooks/useSeo";
import { pathFor } from "../config/site";
import { webpageLd, breadcrumbLd } from "../lib/jsonld";
import PageHero from "../components/PageHero";
import { Section } from "../components/Section";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REASONS = ["correction_request", "translation_issue", "copyright_trademark_concern", "general_feedback"];

export default function ContactPage({ pageKey }) {
  const { lang, d } = useI18n();
  const meta = d.meta.contact;
  const f = d.pages.contact.form;

  const [form, setForm] = useState({ name: "", email: "", reason: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useSeo({
    pageKey,
    title: meta.title,
    description: meta.description,
    jsonLd: [
      webpageLd({ lang, pageKey, title: meta.title, description: meta.description }),
      breadcrumbLd([
        { name: d.ui.nav.home, path: pathFor(lang, "home") },
        { name: d.ui.nav.contact, path: pathFor(lang, "contact") },
      ]),
    ],
  });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = true;
    if (!form.reason) e.reason = true;
    if (!form.message.trim()) e.message = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale: lang });
      toast.success(f.success);
      setForm({ name: "", email: "", reason: "", message: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero testid="contact-hero" title={d.pages.contact.heading} intro={d.pages.contact.intro} />
      <Section>
        <div className="grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-jlo-gold shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-jlo-warm/80 leading-relaxed">{d.pages.contact.rightsHolderNote}</p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-3 space-y-5" noValidate>
            <div>
              <Label htmlFor="c-name" className="text-jlo-warm/80">{f.name}</Label>
              <Input
                id="c-name" data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`mt-2 bg-jlo-bg2 border-jlo-gold/25 text-jlo-warm ${errors.name ? "border-jlo-red" : ""}`}
              />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-jlo-warm/80">{f.email}</Label>
              <Input
                id="c-email" type="email" data-testid="contact-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={`mt-2 bg-jlo-bg2 border-jlo-gold/25 text-jlo-warm ${errors.email ? "border-jlo-red" : ""}`}
              />
            </div>
            <div>
              <Label className="text-jlo-warm/80">{f.reason}</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="contact-reason" className={`mt-2 bg-jlo-bg2 border-jlo-gold/25 text-jlo-warm ${errors.reason ? "border-jlo-red" : ""}`}>
                  <SelectValue placeholder={f.reason} />
                </SelectTrigger>
                <SelectContent className="bg-jlo-bg2 border-jlo-gold/20 text-jlo-warm">
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`reason-${r}`} className="focus:bg-jlo-gold/15 focus:text-jlo-gold">
                      {d.pages.contact.reasons[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="c-message" className="text-jlo-warm/80">{f.message}</Label>
              <Textarea
                id="c-message" rows={5} data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`mt-2 bg-jlo-bg2 border-jlo-gold/25 text-jlo-warm ${errors.message ? "border-jlo-red" : ""}`}
              />
            </div>
            <button
              type="submit" data-testid="contact-submit" disabled={loading}
              className="focus-gold inline-flex items-center justify-center rounded-full bg-jlo-gold px-8 py-3 text-sm font-semibold text-jlo-bg hover:bg-jlo-warm transition-colors disabled:opacity-60"
            >
              {loading ? f.sending : f.submit}
            </button>
          </form>
        </div>
      </Section>
    </>
  );
}
